import {createSelector} from '@reduxjs/toolkit';

import {PLA, STEPS} from 'desktop/pages/checkout/constants/checkout';
import {ApplicationStoreType} from 'desktop/store';

import {CheckoutItemsType} from 'desktop/types/checkoutItemsType';
import {
    OrderReviewTotalsSegmentsType,
    OrderReviewTotalsType,
} from 'desktop/types/orderReviewTotalsType';
import {isUhcEnabled} from 'libs/rollout';

import {MODULE_KEY_CHECKOUT} from '../constants';
import {getCurrentStep} from '../currentStep/currentStep';
import {getOrderReviewItems, isSomeUhcItems} from '../orderReviewItems/orderReviewItems';

import {
    addDiscountTotals,
    GRAND_TOTAL_TITLE,
    SALES_TAX_TITLE,
    SUBTOTAL_TITLE,
    TAX_ROW,
} from './utils/prepareTotals';
import {getCurrentRebateDiscount} from './utils/rebateDiscount';

/**
 * Get order review total
 * @param {ApplicationStoreType} store - current store
 * @return {OrderReviewTotalsType} - order review total
 */
export const getOrderReviewTotals = (store: ApplicationStoreType): OrderReviewTotalsType =>
    store[MODULE_KEY_CHECKOUT].orderReviewTotals;

/**
 * Get shipping protection from totals
 * @param {ApplicationStoreType} store - current store
 * @return {OrderReviewTotalsSegmentsType} - order review total item
 */
export const getTotalsShippingProtection = createSelector(getOrderReviewTotals, totals =>
    totals.totals.find(t => t.code === 'shipping_protection'),
);

/**
 * Get order review total items
 * @param {ApplicationStoreType} store - current store
 * @return {OrderReviewTotalsItemsType[]} - order review total items
 */
export const getOrderReviewTotalItems = createSelector(
    getOrderReviewTotals,
    totals => totals.items,
);

/**
 * Get order item totals by id
 * @param {number} itemId - item id
 * @returns {number} - item order tax
 */
export const getOrderReviewItemTaxById = (itemId: number) =>
    createSelector(
        getOrderReviewTotalItems,
        items => (items.find(item => item.id === itemId) || {tax: 0}).tax,
    );

/**
 * Get order review total items count
 * @param {ApplicationStoreType} store - current store
 * @return {number} - count of items of review total items
 */
export const getOrderReviewTotalItemsCount = createSelector(
    getOrderReviewTotals,
    ({count}) => count,
);

/**
 * Get order review total is loading
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - order review total is loading
 */
export const getOrderReviewTotalsIsLoading = createSelector(
    getOrderReviewTotals,
    totals => totals.status !== 'FULFILLED',
);

/**
 * Get order review total is loaded with tax
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - order review total is loaded with tax
 */
export const getTotalsLoadedWithTax = createSelector(getOrderReviewTotals, totals =>
    Boolean(totals.status === 'FULFILLED' && totals.isWithTaxCollect),
);

/**
 * Get actual order review total rows
 * @param {ApplicationStoreType} store - current store
 * @return {OrderReviewTotalsSegmentsType[]} - order review total rows
 */
export const getActualTotalRows = createSelector(
    getOrderReviewTotals,
    isSomeUhcItems,
    ({copay, totals, totalTax = 0}, someUhcItems) => {
        const actualTotalRows =
            isUhcEnabled() && totals.length && someUhcItems
                ? addDiscountTotals(totals, copay)
                : [...totals];

        actualTotalRows.splice(-1, 0, {...TAX_ROW, price: Number(totalTax)});
        return actualTotalRows;
    },
);

/**
 * It counts sum applying rebate discounts
 * @return {number} rebate discount sum
 */
export const getTotalRebateSum = createSelector(getOrderReviewItems, (items: CheckoutItemsType) =>
    items.reduce((acc: number, item) => {
        if (item.sku.match(PLA) !== null) return acc;

        if (item.rebateDiscounts) {
            return acc + (getCurrentRebateDiscount(item.rebateDiscounts, Number(item.qty)) || 0);
        }

        return acc;
    }, 0),
);

/**
 * Get grand total
 * @param {ApplicationStoreType} store - current store
 * @return {OrderReviewTotalsSegmentsType} - order review grand total
 */
export const getGrandTotal = createSelector(
    getActualTotalRows,
    rows =>
        rows.find(it => it.title === GRAND_TOTAL_TITLE) || {
            area: null,
            code: '0',
            price: 0,
            title: GRAND_TOTAL_TITLE,
        },
);

/**
 * Get grand total tax
 * @param {ApplicationStoreType} store - current store
 * @return {OrderReviewTotalsSegmentsType} - order review grand total
 */
export const getTaxTotal = createSelector(
    getActualTotalRows,
    rows =>
        rows.find(it => it.title === SALES_TAX_TITLE) || {
            area: null,
            code: 'tax',
            price: 0,
            title: SALES_TAX_TITLE,
        },
);

/**
 * Get subtotal
 * @param {ApplicationStoreType} store - current store
 * @return {OrderReviewTotalsSegmentsType} - order review subtotal
 */
export const getSubtotal = createSelector(
    getActualTotalRows,
    rows =>
        rows.find(it => it.title === SUBTOTAL_TITLE) || {
            area: null,
            code: '0',
            price: 0,
            title: SUBTOTAL_TITLE,
        },
);

/**
 * Get is show tax warning
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - is show tax warning
 */
export const getIsShowTaxWarning = createSelector(
    getOrderReviewTotals,
    getCurrentStep,
    ({grandTotal, totalTax}, currentStep) =>
        currentStep === STEPS.PAYMENT_STEP && Boolean(grandTotal - totalTax <= 0 && grandTotal),
);

/**
 * Get including tax
 * @param {ApplicationStoreType} store - current store
 * @return {number} - including tax
 */
export const getIncludingTax = createSelector(
    getOrderReviewTotals,
    ({subtotal, totalTax}) => (subtotal * 100 + totalTax * 100) / 100,
);

/**
 * It counts total after applying rebate discount
 * @return {number} total with applied rebate discount
 */
export const getCartTotalWithRebate = createSelector(
    getTotalRebateSum,
    getGrandTotal,
    (totalRebateDiscount: number, total: OrderReviewTotalsSegmentsType) =>
        total.price - totalRebateDiscount,
);
